/**
 * HistoryConversationsOverlay · 历史会话 slide-down 顶部下拉 + scrim
 *
 * v3 改造（2026-05-08）：从底部 slide-up 全幅 overlay → 顶部 slide-down 短面板 + 蒙层
 *  · 去掉标题栏 / X 关闭按钮 · 视觉更轻
 *  · 面板从 AIChat 容器顶部下拉 · 高度容纳约 5 条，超出滚动
 *  · 面板下方铺蒙层，点蒙层关闭；头部「历史会话」按钮也支持 toggle 关闭
 *  · 数据 / 列表渲染 / formatRelative 沿用旧版本
 *
 * 数据
 *  · open=true 时拉一次 fetchAiChatConversations · 关闭后保留缓存，下次打开重拉
 */

import React, { useEffect, useState } from 'react'
import { Empty, Spin } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectConversationId,
  selectHistoryOverlayOpen,
  setHistoryOverlayOpen
} from '../../../../slices/aiClassify'
import {
  fetchAiChatConversations,
  BackendConversation
} from '../../services/aiChatConversations'
import { useChatActions } from '../AIChat/ChatActionsContext'
import { trackAIChatClick } from '../../utils/trackClassify'
import styles from './HistoryConversationsPopover.module.scss'

const HistoryConversationsOverlay: React.FC = () => {
  const dispatch = useAppDispatch()
  const open = useAppSelector(selectHistoryOverlayOpen)
  const { switchConversation } = useChatActions()
  const currentConvId = useAppSelector(selectConversationId)
  const groupID = useAppSelector((s) => s.group.groupId)

  const [convs, setConvs] = useState<BackendConversation[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!open) return
    if (!groupID) {
      setErrorMsg('未获取到团队 ID')
      return
    }
    let cancelled = false
    const ctrl = new AbortController()
    setLoading(true)
    setErrorMsg('')
    fetchAiChatConversations(groupID, 1, 30, ctrl.signal)
      .then((resp) => {
        if (cancelled) return
        setConvs(Array.isArray(resp.conversations) ? resp.conversations : [])
      })
      .catch((err) => {
        if (cancelled) return
        setErrorMsg((err as Error)?.message || '加载失败')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [open, groupID])

  const handleClose = (): void => {
    trackAIChatClick('closeHistory')
    dispatch(setHistoryOverlayOpen(false))
  }

  const handlePick = (convId: string): void => {
    if (!convId) return
    trackAIChatClick('switchSession')
    switchConversation(convId)
    handleClose()
  }

  if (!open) return null

  return (
    <>
      {/* 面板 · 从 AIChat 容器顶部 slide-down · 高度容纳约 5 条，超出滚动 */}
      <div className={styles.panel} role="dialog" aria-label="历史会话">
        {loading && !convs ? (
          <div className={styles.center}>
            <Spin />
          </div>
        ) : errorMsg ? (
          <div className={styles.center}>加载失败：{errorMsg}</div>
        ) : !convs || convs.length === 0 ? (
          <div className={styles.center}>
            <Empty description="暂无历史会话" />
          </div>
        ) : (
          <ul className={styles.list}>
            {convs.map((c) => {
              const isActive = c.conversation_id === currentConvId
              return (
                <li
                  key={c.conversation_id}
                  className={classNames(styles.item, {
                    [styles.itemActive]: isActive
                  })}
                  onClick={() => handlePick(c.conversation_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePick(c.conversation_id)
                    }
                  }}
                >
                  <span className={styles.title}>
                    {c.title || '无标题会话'}
                  </span>
                  <span className={styles.time}>
                    {formatRelative(c.updated_at)}
                  </span>
                  {isActive && <CheckOutlined className={styles.checkIcon} />}
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {/* 蒙层 · 面板下方覆盖 AIChat 剩余区域 · 点击关闭 */}
      <div
        className={styles.scrim}
        onClick={handleClose}
        aria-hidden
        role="presentation"
      />
    </>
  )
}

function formatRelative(iso: string): string {
  if (!iso) return ''
  const ts = new Date(iso).getTime()
  if (!ts || Number.isNaN(ts)) return ''
  const diff = Date.now() - ts
  if (diff < 60_000) return '刚刚'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} 天前`
  const d = new Date(ts)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

export default HistoryConversationsOverlay
